import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../api.js';
import { exportFilename, toMarkdown, type CampaignExport } from './campaignExport.js';

interface CampaignSummary {
  campaign: { id: string; name: string; premise: string; archivedAt: string | null };
  /** Absent when you own the table but are not playing in it. */
  characterId?: string;
  isOwner: boolean;
}

interface CharacterSummary {
  character: { id: string; name: string; race: string; alignment: string; deathStatus?: string };
  classes: { class: string; level: number }[];
  /** The table this character already sits at, if any. */
  seat?: { campaignId: string; campaignName: string };
}

interface JoinableCampaign {
  id: string;
  name: string;
  premise: string;
}

/** The lobby: the campaigns you are in, and the characters you have. */
export function HomePage() {
  const [campaigns, setCampaigns] = useState<CampaignSummary[] | null>(null);
  const [characters, setCharacters] = useState<CharacterSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [joinable, setJoinable] = useState<JoinableCampaign[]>([]);
  const [joining, setJoining] = useState<string | null>(null);
  const [joinWith, setJoinWith] = useState('');

  const load = useCallback(async () => {
    try {
      const [c, ch, j] = await Promise.all([
        // **The lobby shows what is in play.** An archive that never left the
        // list would not be an archive — but it has to be reachable, or
        // unarchiving is a thing only the API can do. Hence a toggle rather
        // than either extreme.
        api.get<{ campaigns: CampaignSummary[] }>(
          showArchived ? '/api/campaigns?archived=true' : '/api/campaigns',
        ),
        api.get<{ characters: CharacterSummary[] }>('/api/characters'),
        api.get<{ campaigns: JoinableCampaign[] }>('/api/campaigns/joinable'),
      ]);
      setCampaigns(c.campaigns);
      setCharacters(ch.characters);
      setJoinable(j.campaigns);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your table.');
    }
  }, [showArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Runs one lifecycle action and re-reads the list from the server. */
  const act = (id: string, run: () => Promise<unknown>) => {
    setBusy(id);
    setError(null);
    void run()
      .then(() => load())
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'That did not work.'),
      )
      .finally(() => setBusy(null));
  };

  /**
   * Downloads the campaign as Markdown.
   *
   * Rendered rather than saved raw: the endpoint answers JSON and the thing
   * anyone actually wants to keep is the story. See `campaignExport.ts`.
   */
  const exportCampaign = (id: string) =>
    act(id, async () => {
      const data = await api.get<CampaignExport>(`/api/campaigns/${id}/export`);
      const blob = new Blob([toMarkdown(data)], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFilename(data.campaign.name, data.exportedAt);
      // **Appended before clicking and revoked a tick later.** A detached
      // anchor is ignored by some browsers, and revoking in the same turn as
      // the click can cancel the download before it has read the blob — both
      // failures look like "the button does nothing". The revoke still has to
      // happen, or every export holds the whole transcript in memory for the
      // lifetime of the tab.
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    });

  return (
    <div className="space-y-10">
      <section className="space-y-4">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Your campaigns</h1>
          <Link to="/campaigns/new" className="text-sm underline underline-offset-2">
            Start a campaign
          </Link>
          <label className="ml-auto flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            {error}
          </p>
        )}
        {campaigns === null && !error && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">Loading…</p>
        )}
        {campaigns?.length === 0 && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            No campaigns yet. Make a character, or start a table — either way round.
          </p>
        )}
        <ul className="space-y-2">
          {campaigns?.map(({ campaign, isOwner, characterId }) => (
            <li
              key={campaign.id}
              className={`rounded-md border border-neutral-200 p-4 dark:border-neutral-800 ${
                campaign.archivedAt ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-baseline gap-2">
                <Link to={`/campaigns/${campaign.id}`} className="font-medium underline underline-offset-2">
                  {campaign.name}
                </Link>
                {/* ⚠ **Says why the table looks empty.** Owning a campaign you
                    have not joined is an ordinary state now — you can set a
                    table up before anybody has rolled anything — and without
                    this the lobby shows a campaign that answers 404 to almost
                    everything, with nothing saying it is because you have no
                    character in it. */}
                {!characterId && (
                  <span className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    yours to set up — no character seated
                  </span>
                )}
                {campaign.archivedAt && (
                  <span className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">archived</span>
                )}
              </div>
              {campaign.premise && (
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  {campaign.premise}
                </p>
              )}

              {/* **The Owner's.** Export, archive and delete are
                  `requireOwner` on the server, which reads
                  `campaigns.created_by`. The two were the same person in
                  practice, so the wrong
                  flag never showed. A courtesy rather than the guard either
                  way: the server checks. */}
              {isOwner && (
                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                  <button
                    type="button"
                    disabled={busy === campaign.id}
                    onClick={() => exportCampaign(campaign.id)}
                    className="underline underline-offset-2"
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    disabled={busy === campaign.id}
                    onClick={() =>
                      act(campaign.id, () =>
                        api.post(
                          `/api/campaigns/${campaign.id}/${
                            campaign.archivedAt ? 'unarchive' : 'archive'
                          }`,
                        ),
                      )
                    }
                    className="underline underline-offset-2"
                  >
                    {campaign.archivedAt ? 'Unarchive' : 'Archive'}
                  </button>
                  <button
                    type="button"
                    disabled={busy === campaign.id}
                    onClick={() => setDeleting(campaign.id)}
                    className="underline underline-offset-2 text-red-700 dark:text-red-400"
                  >
                    Delete
                  </button>
                </div>
              )}

              {/* **In the page, and it states what it costs rather than asking
                  "are you sure?"** — the same two-step the reroll uses, for the
                  same reason: the choice is irreversible. A `window.confirm`
                  would be a second convention for one decision, and a modal
                  the rest of this app does not have. */}
              {deleting === campaign.id && (
                <div className="mt-2 space-y-2 rounded-md border border-red-700/40 p-3 text-xs">
                  <p>
                    Deleting <strong>{campaign.name}</strong> removes the transcript, the world
                    and <strong>every uploaded map</strong>. None of it comes back. Export first
                    if you want to keep the story.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy === campaign.id}
                      onClick={() => {
                        setDeleting(null);
                        act(campaign.id, () => api.delete(`/api/campaigns/${campaign.id}`));
                      }}
                      className="rounded-md border border-red-700 px-2 py-1 text-red-700 dark:text-red-400"
                    >
                      Delete it
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleting(null)}
                      className="rounded-md border border-neutral-300 px-2 py-1 dark:border-neutral-700"
                    >
                      Keep it
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* **A table nobody else could join was a multiplayer portal with one
          seat.** The plan's lobby is "join one with character selection", a
          player act rather than an invite: the email allowlist is already
          the gate, and an invite system would be a second permission model
          over it. */}
      {joinable.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold tracking-tight">Join a campaign</h2>
          <ul className="space-y-2">
            {joinable.map((campaign) => (
              <li
                key={campaign.id}
                className="rounded-md border border-neutral-200 p-4 dark:border-neutral-800"
              >
                <p className="font-medium">{campaign.name}</p>
                {campaign.premise && (
                  <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                    {campaign.premise}
                  </p>
                )}
                {joining === campaign.id ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                    <label className="sr-only" htmlFor={`join-with-${campaign.id}`}>
                      Character
                    </label>
                    <select
                      id={`join-with-${campaign.id}`}
                      value={joinWith}
                      onChange={(e) => setJoinWith(e.target.value)}
                      className="rounded-md border border-neutral-300 bg-white px-2 py-1 dark:border-neutral-700 dark:bg-neutral-900"
                    >
                      <option value="">choose a character…</option>
                      {/* The dead do not take new seats — that is what the
                          reroll is for, and the server refuses it anyway. */}
                      {(characters ?? [])
                        // **Seated characters are left out, like the dead.** A
                        // character holds one seat, so the server refuses one
                        // that is already at a table — offering it here made
                        // the picker a list of choices most of which could only
                        // fail. Filtered rather than disabled because a `select`
                        // gives a disabled option no room to say why.
                        .filter(({ seat }) => seat === undefined)
                        .filter(({ character }) => (character.deathStatus ?? 'alive') === 'alive')
                        .map(({ character, classes }) => (
                          <option key={character.id} value={character.id}>
                            {character.name} (
                            {classes.map((c) => `${c.class} ${c.level}`).join('/')})
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      disabled={joinWith === '' || busy === campaign.id}
                      onClick={() => {
                        // **The form stays open until the request settles.**
                        // Closing it first put the row back to its "Join"
                        // button while the POST was still in flight, so the
                        // `busy` guard was on a control that no longer
                        // existed — a second click fired a second join, which
                        // answers 409 and paints "you already have a
                        // character at that table" over one that succeeded.
                        const characterId = joinWith;
                        act(campaign.id, () =>
                          api
                            .post(`/api/campaigns/${campaign.id}/join`, { characterId })
                            .finally(() => {
                              setJoining(null);
                              setJoinWith('');
                            }),
                        );
                      }}
                      className="rounded-md border border-emerald-600 px-2 py-1 disabled:opacity-50"
                    >
                      Take a seat
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setJoining(null);
                        setJoinWith('');
                      }}
                      className="rounded-md border border-neutral-300 px-2 py-1 dark:border-neutral-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setJoining(campaign.id)}
                    className="mt-2 text-xs underline underline-offset-2"
                  >
                    Join
                  </button>
                )}
              </li>
            ))}
          </ul>
          {/* Said plainly, because it is the audience model and not a quirk:
              a late joiner reads the table from their arrival onward. */}
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            You will see the story from the moment you sit down, not before it.
          </p>
        </section>
      )}

      <section className="space-y-4">
        <div className="flex items-baseline gap-3">
          <h2 className="text-xl font-semibold tracking-tight">Your characters</h2>
          <Link to="/characters/new" className="text-sm underline underline-offset-2">
            New character
          </Link>
        </div>
        {characters?.length === 0 && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            No characters yet.
          </p>
        )}
        <ul className="space-y-2">
          {characters?.map(({ character, classes }) => (
            <li
              key={character.id}
              className="rounded-md border border-neutral-200 p-4 text-sm dark:border-neutral-800"
            >
              <Link to={`/characters/${character.id}`} className="font-medium underline underline-offset-2">
                {character.name}
              </Link>{' '}
              <span className="text-neutral-600 dark:text-neutral-400">
                {character.race}{' '}
                {classes.map((c) => `${c.class} ${c.level}`).join(' / ')}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <p className="text-sm">
        <Link to="/allowlist" className="underline underline-offset-2">
          Manage the guest list
        </Link>
      </p>
    </div>
  );
}
